import type { FC, MouseEventHandler } from "react"
import { useState, useId } from "react"

import type { Point2d } from "@logic/lib/2d"
import { studioToScreen } from "@logic/lib/design"
import { useRecoilValue } from "recoil"

import {
  areaIndex,
  findInsetAreaFacesState,
} from "~/containers/LegacyDesigner/state/elements"
import { zoomState } from "~/containers/LegacyDesigner/state/view"

import { RainbowFaces } from "../Visuals/Faces"
import { RainbowNodeEdgePairs } from "../Visuals/RainbowNodeEdgePairs"

export const SvgX: FC<{
  node: Point2d
  size: number
  fill: string
  onClick?: MouseEventHandler
}> = (props) => {
  const {
    size,
    node: { x, y },
  } = props

  return (
    <g transform={`translate(${x}, ${y})`}>
      <circle
        r={size}
        fill={props.fill}
        stroke="red"
        strokeWidth={1}
        onClick={props.onClick}
      />
      <line
        x1={-size}
        y1={-size}
        x2={size}
        y2={size}
        stroke="red"
        strokeWidth={1}
      />
      <line
        x1={-size}
        y1={size}
        x2={size}
        y2={-size}
        stroke="red"
        strokeWidth={1}
      />
    </g>
  )
}

export const InsetAreaFilteringVisualization: FC<{ id: string }> = ({ id }) => {
  const {
    nodeEdgePairsThatAreTooClose: rainbowNodeEdgePairs,
    nodesNotInOriginalArea,
    dividedAreas,
  } = useRecoilValue(findInsetAreaFacesState(id)).procedure
  const [shown, setShown] = useState(2)
  const zoom = useRecoilValue(zoomState)

  return (
    <>
      <RainbowFaces faces={dividedAreas} />
      <RainbowNodeEdgePairs pairs={rainbowNodeEdgePairs} />
      {nodesNotInOriginalArea.map(({ node, ray, intersectedEdges }, idx) => {
        const nodeInStudio = studioToScreen.point(node, zoom)
        const rayInStudio = {
          p1: nodeInStudio,
          p2: studioToScreen.point(ray.p2, zoom),
        }
        const intersectedEdgesInStudio = intersectedEdges.map((edge) => ({
          p1: studioToScreen.point(edge.p1, zoom),
          p2: studioToScreen.point(edge.p2, zoom),
        }))

        return (
          <g key={String(idx)}>
            <SvgX
              key={
                String(idx) +
                `_` +
                String(nodeInStudio.x) +
                `_` +
                String(nodeInStudio.y)
              }
              node={nodeInStudio}
              size={5}
              fill="white"
              onClick={() => (console.log(idx), setShown(idx))}
            />
            {shown === idx ? (
              <>
                <line
                  key={
                    String(idx) +
                    `_` +
                    String(nodeInStudio.x) +
                    `_` +
                    String(nodeInStudio.y) +
                    `_` +
                    String(rayInStudio.p2.x) +
                    `_` +
                    String(rayInStudio.p2.y)
                  }
                  x1={nodeInStudio.x}
                  y1={nodeInStudio.y}
                  x2={rayInStudio.p2.x}
                  y2={rayInStudio.p2.y}
                  stroke="red"
                  strokeWidth={1}
                />
                {intersectedEdgesInStudio.map((edge, idx) => (
                  <line
                    key={
                      String(idx) +
                      `_` +
                      String(edge.p1.x) +
                      `_` +
                      String(edge.p1.y) +
                      `_` +
                      String(edge.p2.x) +
                      `_` +
                      String(edge.p2.y)
                    }
                    x1={edge.p1.x}
                    y1={edge.p1.y}
                    x2={edge.p2.x}
                    y2={edge.p2.y}
                    stroke="red"
                    strokeWidth={1}
                  />
                ))}
              </>
            ) : null}
          </g>
        )
      })}
    </>
  )
}

export const InsetAreaFilteringVisualizations: FC = () => {
  const areaIds = useRecoilValue(areaIndex)

  return (
    <>
      {areaIds.map((id) => (
        <InsetAreaFilteringVisualization key={id} id={id} />
      ))}
    </>
  )
}
